/**
 * Custom hook for managing address state in User component
 * Consolidates multiple address-related states into a cohesive structure
 */

import { useState, useCallback, useMemo } from "react";
import * as intrfce from "../Interfaces";

/**
 * Interface for consolidated address state
 */
export interface AddressState {
  // Billing
  billing: {
    selected: intrfce.iSavedAddress | null | "reset";
    saved: intrfce.iSavedAddress[];
    form: intrfce.iAddressFormData;
    contact: Pick<intrfce.iAddressFormData, 'id' | 'firstName' | 'lastName' | 'contact' | 'email' | 'vemail'>;
    readonly: boolean;
    enabled: boolean; // Whether billing is different from shipping
  };
  // Shipping
  shipping: {
    selected: intrfce.iSavedAddress | null | "reset";
    saved: intrfce.iSavedAddress[];
    form: intrfce.iAddressFormData;
    contact: Pick<intrfce.iAddressFormData, 'firstName' | 'lastName' | 'attn' | 'contact' | 'email' | 'vemail'>;
    readonly: boolean;
  };
  // Modal-related
  modal: {
    temporary: intrfce.iSavedAddress | null;
    recommended: intrfce.iAddressFormData | null;
    selectedOption: "yourAddress" | "recommendedAddress";
    // Billing modal state
    recommendedBillingAddress: intrfce.iAddressFormData | null;
    temporaryBillingAddress: intrfce.iSavedAddress | null;
    selectedBillingAddressOption: "yourAddress" | "recommendedAddress";
    billingAddressDifferences: string[];
    shippingAddressDifferences: string[];
  };
}

/**
 * Custom hook for managing address state
 */
export const useAddressState = (
  countryList: Array<{ countryName: string; ISO_CC: string }>,
  locale: string
) => {
  const [addressState, setAddressState] = useState<AddressState>({
    billing: {
      selected: null,
      saved: [],
      form: {
        pincode: "",
        address: "",
        line2: "",
        cityValue: "",
        stateValue: locale === 'zh-tw' ? "TW" : "",
        countryName: countryList[0]?.countryName || "",
        countryValue: countryList[0]?.ISO_CC || "",
      },
      contact: {
        firstName: "",
        lastName: "",
        contact: "",
        email: "",
        vemail: ""
      },
      readonly: false,
      enabled: false,
    },
    shipping: {
      selected: null,
      saved: [],
      form: {
        pincode: "",
        address: "",
        line2: "",
        cityValue: "",
        stateValue: locale === 'zh-tw' ? "TW" : "",
        countryName: countryList[0]?.countryName || "",
        countryValue: countryList[0]?.ISO_CC || "",
      },
      contact: {
        firstName: "",
        lastName: "",
        attn: "",
        contact: "",
        email: "",
        vemail: ""
      },
      readonly: false,
    },
    modal: {
      temporary: null,
      recommended: null,
      selectedOption: "yourAddress",
      recommendedBillingAddress: null,
      temporaryBillingAddress: null,
      selectedBillingAddressOption: "yourAddress",
      billingAddressDifferences: [],
      shippingAddressDifferences: [],
    },
  });

  // Generic setter factory - supports both direct values and functional updates
  const createSetter = useCallback(<T,>(
    section: 'billing' | 'shipping' | 'modal',
    field: string
  ) => {
    return (value: T | ((prev: T) => T)) => {
      if (typeof value === 'function') {
        setAddressState(prev => {
          const sectionData = prev[section] as any;
          return {
            ...prev,
            [section]: {
              ...sectionData,
              [field]: (value as Function)(sectionData[field])
            }
          };
        });
      } else {
        setAddressState(prev => ({
          ...prev,
          [section]: { ...(prev[section] as any), [field]: value }
        }));
      }
    };
  }, []);

  // Billing setters - createSetter already returns memoized functions
  const setSelectedBillingAddress = createSetter<intrfce.iSavedAddress | null | "reset">('billing', 'selected');
  const setSavedBillingAddress = createSetter<intrfce.iSavedAddress[]>('billing', 'saved');

  // Custom setter for formBillingAddress that handles both address and contact fields
  const setFormBillingAddress = useCallback((value: intrfce.iAddressFormData | ((prev: intrfce.iAddressFormData) => intrfce.iAddressFormData)) => {
    if (typeof value === 'function') {
      setAddressState(prev => {
        const currentForm = prev.billing.form;
        const currentContact = prev.billing.contact;
        const merged = { ...currentForm, ...currentContact };
        const updated = (value as Function)(merged);
        // Split back into form and contact
        const { pincode, address, line2, cityValue, stateValue, countryName, countryValue, county, ...contactFields } = updated;
        return {
          ...prev,
          billing: {
            ...prev.billing,
            form: { pincode, address, line2, cityValue, stateValue, countryName, countryValue, county },
            contact: contactFields as Pick<intrfce.iAddressFormData, 'id' | 'firstName' | 'lastName' | 'contact' | 'email' | 'vemail'>
          }
        };
      });
    } else {
      // Split the merged object back into form and contact
      // Use default empty strings to ensure all fields are cleared when passed as direct value
      const { 
        pincode = "", 
        address = "", 
        line2 = "", 
        cityValue = "", 
        stateValue = "", 
        countryName = "", 
        countryValue = "", 
        county, 
        firstName = "", 
        lastName = "", 
        contact = "", 
        email = "", 
        vemail = "" 
      } = value;
      setAddressState(prev => ({
        ...prev,
        billing: {
          ...prev.billing,
          form: { 
            pincode, 
            address, 
            line2, 
            cityValue, 
            stateValue, 
            countryName, 
            countryValue, 
            county: county !== undefined ? county : prev.billing.form.county 
          },
          contact: {
            firstName: firstName || "",
            lastName: lastName || "",
            contact: contact || "",
            email: email || "",
            vemail: vemail || ""
          }
        }
      }));
    }
  }, []);

  const setIsBillingReadonly = createSetter<boolean>('billing', 'readonly');

  // Shipping setters
  const setSelectedShippingAddress = createSetter<intrfce.iSavedAddress | null | "reset">('shipping', 'selected');
  const setSavedShippingAddress = createSetter<intrfce.iSavedAddress[]>('shipping', 'saved');

  // Custom setter for formShippingAddress that handles both address and contact fields
  const setFormShippingAddress = useCallback((value: intrfce.iAddressFormData | ((prev: intrfce.iAddressFormData) => intrfce.iAddressFormData)) => {
    if (typeof value === 'function') {
      setAddressState(prev => {
        const currentForm = prev.shipping.form;
        const currentContact = prev.shipping.contact;
        const merged = {
          ...currentForm,
          firstName: currentContact.firstName,
          lastName: currentContact.lastName,
          attn: currentContact.attn,
          contact: currentContact.contact,
          email: currentContact.email,
          vemail: currentContact.vemail,
        };
        const updated = (value as Function)(merged);
        // Split back into form and contact
        const { pincode, address, line2, cityValue, stateValue, countryName, countryValue, county, firstName, lastName, attn, contact, email, vemail } = updated;
        return {
          ...prev,
          shipping: {
            ...prev.shipping,
            form: { pincode, address, line2, cityValue, stateValue, countryName, countryValue, county },
            contact: {
              firstName: firstName || "",
              lastName: lastName || "",
              attn: attn !== undefined ? attn : "",
              contact: contact || "",
              email: email || "",
              vemail: vemail || ""
            }
          }
        };
      });
    } else {
      // Split the merged object back into form and contact
      // Use default empty strings to ensure all fields are cleared when passed as direct value
      const { 
        pincode = "", 
        address = "", 
        line2 = "", 
        cityValue = "", 
        stateValue = "", 
        countryName = "", 
        countryValue = "", 
        county, 
        firstName = "", 
        lastName = "", 
        attn = "", 
        contact = "", 
        email = "", 
        vemail = "" 
      } = value;
      setAddressState(prev => ({
        ...prev,
        shipping: {
          ...prev.shipping,
          form: { 
            pincode, 
            address, 
            line2, 
            cityValue, 
            stateValue, 
            countryName, 
            countryValue, 
            county: county !== undefined ? county : prev.shipping.form.county 
          },
          contact: {
            firstName: firstName || "",
            lastName: lastName || "",
            attn: attn !== undefined ? attn : "",
            contact: contact || "",
            email: email || "",
            vemail: vemail || ""
          }
        }
      }));
    }
  }, []);

  const setIsShippingReadonly = createSetter<boolean>('shipping', 'readonly');
  const setIsBilling = createSetter<boolean>('billing', 'enabled');

  // Modal setters
  const setTemporaryAddress = createSetter<intrfce.iSavedAddress | null>('modal', 'temporary');
  const setRecommendedShippingAddress = createSetter<intrfce.iAddressFormData | null>('modal', 'recommended');
  const setSelectedAddressOption = useCallback(
    (option: string) => createSetter<"yourAddress" | "recommendedAddress">('modal', 'selectedOption')(option as "yourAddress" | "recommendedAddress"),
    [createSetter]
  );
  const setRecommendedBillingAddress = createSetter<intrfce.iAddressFormData | null>('modal', 'recommendedBillingAddress');
  const setTemporaryBillingAddress = createSetter<intrfce.iSavedAddress | null>('modal', 'temporaryBillingAddress');
  const setSelectedBillingAddressOption = createSetter<"yourAddress" | "recommendedAddress">('modal', 'selectedBillingAddressOption');
  const setBillingAddressDifferences = createSetter<string[]>('modal', 'billingAddressDifferences');
  const setShippingAddressDifferences = createSetter<string[]>('modal', 'shippingAddressDifferences');

  // Memoize form objects to prevent unnecessary rerenders
  // Only create new references when the actual data changes
  // Merge billing address and contact data into a single object
  const memoizedFormBillingAddress = useMemo(
    () => ({
      ...addressState.billing.form,
      ...addressState.billing.contact,
    }),
    [ // eslint-disable-line react-hooks/exhaustive-deps
      addressState.billing.form.pincode,
      addressState.billing.form.address,
      addressState.billing.form.line2,
      addressState.billing.form.cityValue,
      addressState.billing.form.stateValue,
      addressState.billing.form.countryName,
      addressState.billing.form.countryValue,
      addressState.billing.contact.id,
      addressState.billing.contact.firstName,
      addressState.billing.contact.lastName,
      addressState.billing.contact.contact,
      addressState.billing.contact.email,
      addressState.billing.contact.vemail,
    ]
  );

  // Merge shipping address and contact data into a single object
  const memoizedFormShippingAddress = useMemo(
    () => ({
      ...addressState.shipping.form,
      firstName: addressState.shipping.contact.firstName,
      lastName: addressState.shipping.contact.lastName,
      attn: addressState.shipping.contact.attn,
      contact: addressState.shipping.contact.contact,
      email: addressState.shipping.contact.email,
      vemail: addressState.shipping.contact.vemail,
    }),
    [ // eslint-disable-line react-hooks/exhaustive-deps
      addressState.shipping.form.pincode,
      addressState.shipping.form.address,
      addressState.shipping.form.line2,
      addressState.shipping.form.cityValue,
      addressState.shipping.form.stateValue,
      addressState.shipping.form.countryName,
      addressState.shipping.form.countryValue,
      addressState.shipping.contact.firstName,
      addressState.shipping.contact.lastName,
      addressState.shipping.contact.attn,
      addressState.shipping.contact.contact,
      addressState.shipping.contact.email,
      addressState.shipping.contact.vemail,
    ]
  );

  const memoizedSavedBillingAddress = useMemo(
    () => addressState.billing.saved,
    [JSON.stringify(addressState.billing.saved)] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const memoizedSavedShippingAddress = useMemo(
    () => addressState.shipping.saved,
    [JSON.stringify(addressState.shipping.saved)] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const memoizedRecommendedShippingAddress = useMemo(
    () => addressState.modal.recommended,
    [JSON.stringify(addressState.modal.recommended)] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return {
    // Billing state
    selectedBillingAddress: addressState.billing.selected,
    setSelectedBillingAddress,
    savedBillingAddress: memoizedSavedBillingAddress,
    setSavedBillingAddress,
    formBillingAddress: memoizedFormBillingAddress,
    setFormBillingAddress,
    isBillingReadonly: addressState.billing.readonly,
    setIsBillingReadonly,

    // Shipping state
    selectedShippingAddress: addressState.shipping.selected,
    setSelectedShippingAddress,
    savedShippingAddress: memoizedSavedShippingAddress,
    setSavedShippingAddress,
    formShippingAddress: memoizedFormShippingAddress,
    setFormShippingAddress,
    isShippingReadonly: addressState.shipping.readonly,
    setIsShippingReadonly,
    isBilling: addressState.billing.enabled,
    setIsBilling,

    // Modal state
    temporaryAddress: addressState.modal.temporary,
    setTemporaryAddress,
    recommendedShippingAddress: memoizedRecommendedShippingAddress,
    setRecommendedShippingAddress,
    selectedAddressOption: addressState.modal.selectedOption,
    setSelectedAddressOption,
    // Billing modal state
    recommendedBillingAddress: addressState.modal.recommendedBillingAddress,
    setRecommendedBillingAddress,
    temporaryBillingAddress: addressState.modal.temporaryBillingAddress,
    setTemporaryBillingAddress,
    selectedBillingAddressOption: addressState.modal.selectedBillingAddressOption,
    setSelectedBillingAddressOption,
    billingAddressDifferences: addressState.modal.billingAddressDifferences,
    setBillingAddressDifferences,
    shippingAddressDifferences: addressState.modal.shippingAddressDifferences,
    setShippingAddressDifferences,
  };
};
